import type { ReactNode } from 'react';

/** A tool row from the legacy m_chirotools table. */
export interface ChiroTool {
  id: number;
  name: string;
  /** Body-part group: 1=Skull, 2=Cervical, 3=Thoracic, 4=Lumbar,
   *  5=Lower Ext, 6=Upper Ext, 7=Special. */
  bodyPart: number;
  description: string;
  reference: string;
  visible: boolean;
}

/** A step row from the legacy m_chirosteps table. */
export interface ChiroStep {
  order: number;
  prompt: string;
  /** Marker position on the anatomical reference thumbnail (pixels within
   *  the thumbnail's own coordinate space). */
  markerX: number;
  markerY: number;
}

/** A point placed by the user, in world (mm) coordinates. */
export interface ChiroPoint {
  worldX: number;
  worldY: number;
  worldZ: number;
}

/** What the handler emits after all clicks are placed. */
export interface ChiroResult {
  /** Human-readable measurement result (angle in degrees, distance in mm, etc.). */
  summary: string;
  /** Individual result rows for a template / table. */
  rows?: { label: string; value: string }[];
}

/** A handler wires a tool's step count + rendering + math together. */
export interface ChiroToolHandler {
  toolId: number;
  /** How many clicks this tool requires. Must match steps.json for that tool. */
  expectedPointCount: number;
  /** Compute derived geometry to render alongside the placed points.
   *  Returns SVG-drawable primitives in world (mm) coordinates. The wizard
   *  transforms world → canvas each render tick via viewport.worldToCanvas. */
  render: (points: ChiroPoint[]) => ChiroDrawable[];
  /** Compute the final measurement summary once all points are placed. */
  compute: (points: ChiroPoint[]) => ChiroResult;
}

/** A drawable primitive in world coordinates. */
export type ChiroDrawable =
  | { kind: 'line'; a: ChiroPoint; b: ChiroPoint; style?: DrawStyle }
  | { kind: 'label'; at: ChiroPoint; text: string; style?: DrawStyle };

export interface DrawStyle {
  color?: string;
  dashed?: boolean;
  fontSize?: number;
}

/** Body-part groupings for the picker's region tabs. */
export const BODY_PARTS: { id: number; label: string }[] = [
  { id: 1, label: 'Skull' },
  { id: 2, label: 'Cervical' },
  { id: 3, label: 'Thoracic' },
  { id: 4, label: 'Lumbar' },
  { id: 5, label: 'Lower Ext.' },
  { id: 6, label: 'Upper Ext.' },
  { id: 7, label: 'Special' },
];

/** Used by ChiroWizard to render arbitrary content in the prompt panel. */
export type PromptPanelExtra = ReactNode;
